/**
 * Pure hex-colour math — the single source shared by the chart palette (`chartColors.ts`) and the
 * tag/picker colour logic (`Punchup/primitives/colorUtils.ts`). No domain knowledge, no dependencies.
 *
 * Both layers darken/lighten/measure colours; keeping the maths here means the tag's "readable shade"
 * (`chipColor`) and the chart's "contrast outline" (`chartContrastStroke`) derive from the SAME
 * primitives rather than re-implementing channel maths in two places.
 */

/** Parses `#RRGGBB` to `[r, g, b]` (0–255); null for any non-6-digit input. */
export function hexToRgb(hex: string): [number, number, number] | null {
	const clean = hex.replace('#', '');
	if (clean.length !== 6) return null;
	// Slice directly (no regex match → no unreachable null-narrowing branch): a 6-char string always yields
	// three 2-char channels.
	return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
}

/** Builds `#RRGGBB` from r/g/b, clamping to 0–255 and rounding. */
export function rgbToHex(r: number, g: number, b: number): string {
	return (
		'#' +
		[r, g, b]
			.map((v) =>
				Math.round(Math.max(0, Math.min(255, v)))
					.toString(16)
					.padStart(2, '0')
			)
			.join('')
	);
}

/** WCAG relative luminance per IEC 61966-2-1 (0 = black, 1 = white). */
export function wcagLuminance(hex: string): number {
	const c = hex.replace('#', '').padEnd(6, '0');
	const r = parseInt(c.slice(0, 2), 16) / 255;
	const g = parseInt(c.slice(2, 4), 16) / 255;
	const b = parseInt(c.slice(4, 6), 16) / 255;
	const lin = (x: number) => (x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4);
	return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Blend a hex colour toward WHITE by `factor` (0 = original, 1 = full white) — the lighter, softer shade. */
export function lightenHex(hex: string, factor: number): string {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const [r, g, b] = rgb;
	return rgbToHex(r + factor * (255 - r), g + factor * (255 - g), b + factor * (255 - b));
}

/** Blend a hex colour toward BLACK by `factor` (0 = original, 1 = full black) — the darker, deeper shade. */
export function darkenHex(hex: string, factor: number): string {
	const rgb = hexToRgb(hex);
	if (!rgb) return hex;
	const [r, g, b] = rgb;
	return rgbToHex(r * (1 - factor), g * (1 - factor), b * (1 - factor));
}
